import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../lib/api";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Calendar } from "../../components/ui/calendar.jsx";
import { Switch } from "../../components/ui/switch";

export default function StaffReview() {
  const [authorized, setAuthorized] = useState(() => localStorage.getItem("guild_staff_auth") === "1");
  const [passInput, setPassInput] = useState("");

  const STAFF_PASS = process.env.REACT_APP_STAFF_PASS || "L3tm3b3!";

  const [items, setItems] = useState([]);
  const [filterStatus, setFilterStatus] = useState("Pending");
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState({});

  const [exportType, setExportType] = useState("All");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [onlyApprovedCSV, setOnlyApprovedCSV] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const url = `${API}/applications${filterStatus && filterStatus !== 'All' ? `?status=${encodeURIComponent(filterStatus)}` : ''}`;
      const res = await axios.get(url);
      setItems(res.data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (authorized) load(); }, [filterStatus, authorized]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/applications/${id}/status`, { status, note: notes[id] });
      toast.success(`Status set to ${status}`);
      await load();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const [proofOpen, setProofOpen] = useState(false);
  const [proofData, setProofData] = useState(null);

  const openProof = async (id) => {
    try {
      const res = await axios.get(`${API}/applications/${id}`);
      if (res.data && res.data.proof) setProofData({ app: res.data, proof: res.data.proof });
      else setProofData({ app: res.data, proof: null });
      setProofOpen(true);
    } catch {
      toast.error("Failed to load proof");
    }
  };

  const authorize = () => {
    if (passInput.trim() === STAFF_PASS) {
      localStorage.setItem("guild_staff_auth", "1");
      setAuthorized(true);
      toast.success("Staff access granted");
    } else {
      toast.error("Incorrect passcode");
    }
  };

  const logout = () => {
    localStorage.removeItem("guild_staff_auth");
    setAuthorized(false);
    toast.success("Logged out of staff access");
  };

  const setPreset = (days) => {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - (days - 1));
    setDateRange({ from, to });
  };

  const exportCSV = async () => {
    try {
      const headers = [
        "id","applicant_type","status","created_at",
        "institution_name","business_name","dean_name","owner_name",
        "sl_legacy_name","institution_location","business_location",
        "certification_type","statement_of_intent","ooc_notes","staff_note",
        "has_proof","proof_type","proof_size_bytes"
      ];

      const inRange = (iso) => {
        if (!dateRange?.from && !dateRange?.to) return true;
        const d = new Date(iso);
        if (dateRange?.from && d < new Date(new Date(dateRange.from).setHours(0,0,0,0))) return false;
        if (dateRange?.to && d > new Date(new Date(dateRange.to).setHours(23,59,59,999))) return false;
        return true;
      };

      let baseList = items;
      if (onlyApprovedCSV) {
        const resAll = await axios.get(`${API}/applications?status=Approved`);
        baseList = resAll.data;
      }

      const filtered = baseList
        .filter(row => exportType === 'All' || row.applicant_type === exportType)
        .filter(row => inRange(row.created_at));

      const details = await Promise.all(
        filtered.map(r => axios.get(`${API}/applications/${r.id}`).then(res => res.data).catch(() => null))
      );
      const detailMap = new Map(details.filter(Boolean).map(d => [d.id, d]));

      let withProofCount = 0, instCount = 0, busCount = 0, staffNoteCount = 0;

      const rows = filtered.map(row => {
        const f = row.fields || {};
        const det = detailMap.get(row.id);
        const proof = det?.proof;
        const hasProof = !!(proof && (proof.base64_data || proof.size));
        if (hasProof) withProofCount += 1;
        if (row.applicant_type === 'Institution') instCount += 1; else if (row.applicant_type === 'Business') busCount += 1;
        const proofType = proof?.content_type || '';
        const proofSize = proof?.size || '';
        if (f.staff_note && String(f.staff_note).trim().length > 0) staffNoteCount += 1;

        return [
          row.id, row.applicant_type, row.status, row.created_at || '',
          f.institution_name || '', f.business_name || '', f.dean_name || '', f.owner_name || '',
          f.sl_legacy_name || '', f.institution_location || '', f.business_location || '',
          f.certification_type || '', f.statement_of_intent || '', f.ooc_notes || '', f.staff_note || '',
          hasProof ? 'Yes' : 'No', proofType, proofSize
        ];
      });

      const totalCount = rows.length;
      const dateFromStr = dateRange?.from ? new Date(dateRange.from).toISOString().slice(0,10) : '';
      const dateToStr = dateRange?.to ? new Date(dateRange.to).toISOString().slice(0,10) : '';

      const esc = (v) => `"${String(v).replace(/"/g,'""')}"`;
      const summaryLines = [
        ["Report","Howling Hills Academic Guild - Applications Export"],
        ["Generated", new Date().toISOString()],
        ["Only Approved in CSV", onlyApprovedCSV ? "Yes" : "No"],
        ["Export Type", exportType],
        ["Date From", dateFromStr],
        ["Date To", dateToStr],
        ["Total Exported", totalCount],
        ["Institutions", instCount],
        ["Businesses", busCount],
        ["With Proof", withProofCount],
        ["With Staff Note", staffNoteCount],
      ].map(r => r.map(esc).join(',')).join('\n') + '\n\n';

      const dataCSV = [headers.join(','), ...rows.map(r => r.map(esc).join(','))].join('\n');
      const csv = summaryLines + dataCSV;

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `guild-applications-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 0);
      toast.success("CSV exported");
    } catch (e) {
      console.error(e);
      toast.error("Failed to export CSV");
    }
  };

  if (!authorized) {
    return (
      <div className="py-10">
        <div className="text-center">
          <span className="badge-red">Restricted</span>
          <h2 className="section-title text-3xl guild-display">Staff Access</h2>
          <p className="text-yellow-100/80 mt-2 max-w-2xl mx-auto">Enter the passcode to review and manage applications.</p>
        </div>
        <Card className="guild-card mt-6 max-w-md mx-auto">
          <CardHeader><CardTitle className="text-white">Enter Passcode</CardTitle></CardHeader>
          <CardContent>
            <Label className="text-white">Passcode</Label>
            <Input className="mt-2 guild-input" type="password" value={passInput} onChange={(e) => setPassInput(e.target.value)} placeholder="Enter passcode" />
            <div className="mt-4 flex gap-2">
              <Button onClick={authorize} className="btn-blue">Enter</Button>
              <Button variant="secondary" onClick={() => setPassInput("")}>Clear</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="py-10">
      <div className="text-center">
        <span className="badge-red">Staff</span>
        <h2 className="section-title text-3xl guild-display">Review Applications</h2>
        <p className="text-yellow-100/80 mt-2 max-w-2xl mx-auto">Mark applications as Approved, Denied, or Revoked. Approved entries appear on the public registry.</p>
      </div>

      <div className="mt-4 flex flex-wrap gap-3 items-end">
        <div className="w-60">
          <Label className="text-white">Filter by status</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full mt-2 guild-input">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#2a0c0c] border-yellow-500/30 text-yellow-100">
              <SelectItem value="Pending">Pending</SelectItem>
              <SelectItem value="Approved">Approved</SelectItem>
              <SelectItem value="Denied">Denied</SelectItem>
              <SelectItem value="Revoked">Revoked</SelectItem>
              <SelectItem value="All">All</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="w-52">
          <Label className="text-white">Export type</Label>
          <Select value={exportType} onValueChange={setExportType}>
            <SelectTrigger className="w-full mt-2 guild-input">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent className="bg-[#2a0c0c] border-yellow-500/30 text-yellow-100">
              <SelectItem value="All">All</SelectItem>
              <SelectItem value="Institution">Institution</SelectItem>
              <SelectItem value="Business">Business</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-white">Date range (export)</Label>
          <div className="mt-2 flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline">Pick dates</Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="range" selected={dateRange} onSelect={setDateRange} numberOfMonths={2} />
              </PopoverContent>
            </Popover>
            <Button variant="ghost" onClick={() => setDateRange({from: null, to: null})}>Clear</Button>
            <Button variant="secondary" size="sm" onClick={() => setPreset(7)}>Last 7 days</Button>
            <Button variant="secondary" size="sm" onClick={() => setPreset(30)}>Last 30 days</Button>
          </div>
        </div>

        <div className="flex items-center gap-2 ml-2">
          <Switch id="onlyApprovedCSV" checked={onlyApprovedCSV} onCheckedChange={setOnlyApprovedCSV} />
          <Label htmlFor="onlyApprovedCSV" className="text-white">Only Approved in CSV</Label>
        </div>

        <Button onClick={load} disabled={loading} className="btn-blue">Refresh</Button>
        <Button variant="outline" onClick={exportCSV}>Export CSV</Button>
        <Button variant="destructive" onClick={logout}>Log out staff</Button>
      </div>

      <Card className="guild-card mt-6">
        <CardHeader><CardTitle className="text-xl text-white">Applications ({items.length})</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableCaption>Recent applications</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead className="text-white">Type</TableHead>
                <TableHead className="text-white">Name</TableHead>
                <TableHead className="text-white">Responsible</TableHead>
                <TableHead className="text-white">Location</TableHead>
                <TableHead className="w-48 text-white">Staff Note</TableHead>
                <TableHead className="text-white">Status</TableHead>
                <TableHead className="text-right text-white">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((row) => {
                const f = row.fields || {};
                const name = row.applicant_type === 'Institution' ? f.institution_name : f.business_name;
                const person = row.applicant_type === 'Institution' ? f.dean_name : f.owner_name;
                const location = row.applicant_type === 'Institution' ? f.institution_location : f.business_location;
                return (
                  <TableRow key={row.id}>
                    <TableCell className="text-yellow-100/90">{row.applicant_type}</TableCell>
                    <TableCell className="text-yellow-100/90">{name || '-'}</TableCell>
                    <TableCell className="text-yellow-100/90">{person || '-'}</TableCell>
                    <TableCell className="text-yellow-100/90">{location || '-'}</TableCell>
                    <TableCell>
                      <Input placeholder="Optional note" value={notes[row.id] || ''} onChange={(e) => setNotes(prev => ({ ...prev, [row.id]: e.target.value }))} className="guild-input" />
                    </TableCell>
                    <TableCell className="text-yellow-100/90">{row.status}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="secondary" onClick={() => openProof(row.id)}>View proof</Button>
                        <Button size="sm" onClick={() => setStatus(row.id, 'Approved')}>Approve</Button>
                        <Button size="sm" variant="secondary" onClick={() => setStatus(row.id, 'Denied')}>Deny</Button>
                        <Button size="sm" variant="destructive" onClick={() => setStatus(row.id, 'Revoked')}>Revoke</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-yellow-100/70">No applications to show.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={proofOpen} onOpenChange={setProofOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-white">Proof of 101 Base Course</DialogTitle>
            <DialogDescription className="text-yellow-100/80">{proofData?.app?.fields?.institution_name || proofData?.app?.fields?.business_name}</DialogDescription>
          </DialogHeader>
          <div>
            {!proofData?.proof && (<div className="text-yellow-100/80">No proof file was uploaded.</div>)}
            {proofData?.proof && (
              <div className="space-y-3">
                <div className="text-sm text-yellow-100/80">
                  <div><span className="font-semibold">Filename:</span> {proofData.proof.filename}</div>
                  <div><span className="font-semibold">Type:</span> {proofData.proof.content_type}</div>
                  <div><span className="font-semibold">Size:</span> {proofData.proof.size} bytes</div>
                </div>
                {proofData.proof.content_type?.startsWith('image/') ? (
                  <img alt="Uploaded proof" className="rounded-md border" src={`data:${proofData.proof.content_type};base64,${proofData.proof.base64_data}`} />
                ) : (
                  <a className="underline text-blue-300" href={`data:${proofData.proof.content_type};base64,${proofData.proof.base64_data}`} download={proofData.proof.filename || 'proof'}>Download file</a>
                )}
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={() => setProofOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}